import React from 'react';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import {
    BillingSettings,
    InvoiceLineSummary,
    toCurrency,
    toDisplayDate,
    prixUnitaireHt,
    prixUnitaireTtc,
} from './invoiceTypes';

/**
 * Le document de facture lui-même — ce que le client reçoit.
 *
 * Il était écrit DEUX FOIS dans CreateInvoice.tsx : une fois pour l'aperçu à l'écran, une fois pour
 * le rendu hors écran qui alimente le téléchargement automatique. Les deux copies avaient divergé —
 * celle du rendu hors écran avait perdu le logo et la pastille « Facture acquittée ». Une seule
 * source désormais : les deux modes appellent ce composant, la divergence ne peut plus revenir.
 *
 * C'est aussi lui qui est capturé pour l'impression et le PDF (classe .invoice-print-area), d'où le
 * passage d'une ref.
 */

export interface InvoiceDocumentProps {
    settings: BillingSettings;
    user: any;
    accentColor: string;
    accentSoftBg: string;
    accentSoftBorder: string;

    invoiceNumber: string;
    invoiceDate: string;
    coverCount: string;

    clientName: string;
    clientAddress: string;
    clientCity: string;
    clientPostalCode: string;
    clientCountry: string;
    clientType: string;
    clientSiret: string;
    clientVatNumber: string;
    deliveryAddress: string;
    purchaseOrderRef: string;

    lineSummaries: InvoiceLineSummary[];
    displayTvaRates: number[];
    previewHasContent: boolean;

    totalHt: number;
    totalTva: number;
    totalTtcBrut: number;
    alreadyPaidValue: number;
    netToPay: number;
    isSettled: boolean;
    bottomLabel: string;
    bottomColor: string;

    isFranchiseTva: boolean;
    bankDetails: string;

    // Mentions légales obligatoires (Code de commerce, art. L441-9/L441-10) ajoutées le 2026-09-24 :
    // absentes du document jusqu'ici alors que les données existaient déjà (payment_delay_days) ou
    // étaient saisies sans être imprimées (operationNature). Voir aussi isPrestation ci-dessous.
    isPrestation?: boolean;
    operationNature?: string;

    /**
     * `screen` : l'aperçu visible dans l'écran de création.
     * `offscreen` : le même document rendu hors champ, quand il faut le capturer sans l'afficher.
     */
    variant?: 'screen' | 'offscreen';
}

const OPERATION_NATURE_LABELS: Record<string, string> = {
    services: 'Prestation de services',
    biens: 'Livraison de biens',
    mixte: 'Livraison de biens et prestation de services',
};

export const InvoiceDocument = React.forwardRef<HTMLDivElement, InvoiceDocumentProps>((props, ref) => {
    const {
        settings, user, accentColor, accentSoftBg, accentSoftBorder,
        invoiceNumber, invoiceDate, coverCount,
        clientName, clientAddress, clientCity, clientPostalCode, clientCountry,
        clientType, clientSiret, clientVatNumber, deliveryAddress, purchaseOrderRef,
        lineSummaries, displayTvaRates, previewHasContent,
        totalHt, totalTva, totalTtcBrut, alreadyPaidValue, netToPay, isSettled, bottomLabel, bottomColor,
        isFranchiseTva, bankDetails,
        isPrestation, operationNature,
        variant = 'screen',
    } = props;

    const offscreen = variant === 'offscreen';

    // Date à laquelle le règlement doit intervenir (mention obligatoire) : delai_paiement_jours de
    // l'établissement, compté depuis la date de facture. N'a pas de sens à afficher une fois la
    // facture réglée — le badge "Facture acquittée" prend le relais.
    const echeanceDate = React.useMemo(() => {
        if (isSettled || !invoiceDate) return '';
        const delayDays = Number.isFinite(Number(settings.payment_delay_days)) ? Number(settings.payment_delay_days) : 30;
        const d = new Date(`${invoiceDate}T00:00:00`);
        if (Number.isNaN(d.getTime())) return '';
        d.setDate(d.getDate() + delayDays);
        return toDisplayDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }, [isSettled, invoiceDate, settings.payment_delay_days]);

    const isProfessionalClient = clientType === 'PROFESSIONNEL';
    const operationNatureLabel = isPrestation && operationNature ? OPERATION_NATURE_LABELS[operationNature] : '';

    return (
        <div
            ref={ref}
            className={offscreen
                ? 'invoice-print-area bg-white flex flex-col'
                : 'invoice-print-area bg-white p-[2mm] w-full max-w-[210mm] mx-auto shadow-xl rounded-lg print:shadow-none print:rounded-none print:p-[2mm] print:m-0 print:w-full flex flex-col min-h-[294mm] overflow-hidden relative border border-slate-100'}
            style={offscreen ? { width: '210mm', minHeight: '291mm', padding: '10mm', boxSizing: 'border-box' } : undefined}
        >
            <div className="invoice-keep-together flex justify-between items-start mb-6 gap-6">
                <div className="flex flex-col min-w-0">
                    {settings.logo_url || user?.logoUrl ? (
                        <img src={resolveLogoUrl(settings.logo_url || user?.logoUrl)} alt="Logo" className="max-h-28 max-w-[250px] mb-2 object-contain self-start" />
                    ) : (
                        <div className="text-sm font-black tracking-[0.2em] text-slate-500 mb-2">LOGO</div>
                    )}
                    <h1 className="text-xl font-bold text-slate-800">{settings.company_name || user?.company_name || user?.name || 'Nom de l’établissement'}</h1>
                    <div className="text-slate-600 text-xs mt-1 leading-snug">
                        <p>{settings.address || '-'}</p>
                        <p>{[settings.postal_code, settings.city].filter(Boolean).join(' ') || '-'}</p>
                    </div>
                    {settings.phone && (
                        <p className="mt-1 text-xs text-slate-600 font-medium tracking-tight">Tél : {settings.phone}</p>
                    )}
                </div>

                <div className="text-right shrink-0">
                    <h2
                        className="text-[32px] font-black uppercase mb-2 leading-none tracking-[-0.035em] print:text-[32px]"
                        style={{
                            color: accentColor,
                            fontFamily: 'Arial Black, Inter, Arial, Helvetica, sans-serif',
                            fontWeight: 900,
                        }}
                    >
                        FACTURE
                    </h2>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 inline-block text-left min-w-[190px] shadow-sm">
                        <div className="mb-2">
                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">Numéro de facture</p>
                            <p className="font-mono text-base font-bold text-slate-900">{invoiceNumber || 'F-XXXXXX'}</p>
                        </div>
                        <div className={`grid ${settings.enable_cover_count || echeanceDate ? 'grid-cols-2' : 'grid-cols-1'} gap-3 pt-2 border-t border-slate-200/60`}>
                            <div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">Date</p>
                                <p className="font-bold text-slate-900 text-xs">{toDisplayDate(invoiceDate)}</p>
                            </div>
                            {echeanceDate && (
                                <div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">Échéance</p>
                                    <p className="font-bold text-slate-900 text-xs">{echeanceDate}</p>
                                </div>
                            )}
                            {settings.enable_cover_count && (
                                <div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">Couverts</p>
                                    <p className="font-bold text-slate-900 text-xs">{coverCount || '-'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {(clientName || clientAddress || clientCity || clientPostalCode || deliveryAddress || purchaseOrderRef) && (
                <div className="invoice-keep-together mb-6 flex justify-end">
                    <div className="w-full md:w-1/2 p-4 rounded-xl border-2 border-slate-50 bg-white shadow-sm">
                        <h3 className="text-[9px] font-black uppercase tracking-widest mb-2" style={{ color: accentColor, opacity: 0.7 }}>Destinataire</h3>
                        <p className="font-bold text-lg text-slate-900 mb-0.5">{clientName || '-'}</p>
                        <div className="text-slate-600 text-xs whitespace-pre-line leading-relaxed">
                            <p>{clientAddress || '-'}</p>
                            <p>{[clientPostalCode, clientCity, clientCountry].filter(Boolean).join(' ') || '-'}</p>
                            {clientType === 'PROFESSIONNEL' && (clientSiret || clientVatNumber) && (
                                <p className="mt-1">{[clientSiret ? `SIRET ${clientSiret}` : '', clientVatNumber ? `TVA ${clientVatNumber}` : ''].filter(Boolean).join(' • ')}</p>
                            )}
                            {deliveryAddress && <p className="mt-1">Livraison : {deliveryAddress}</p>}
                            {purchaseOrderRef && <p className="mt-1">Réf. commande : {purchaseOrderRef}</p>}
                            {operationNatureLabel && <p className="mt-1">Nature de l'opération : {operationNatureLabel}</p>}
                            {settings.enable_cover_count && <p className="mt-1 font-semibold">Nombre de couverts : {coverCount || '-'}</p>}
                        </div>
                    </div>
                </div>
            )}

            {/* Conteneur défilant : colonnes fixes (quantité, prix, TVA, total) qui ne
                peuvent pas se replier — sans lui la facture débordait sur téléphone.
                À l'impression, la règle .invoice-scroll le neutralise. */}
            <div className="invoice-scroll overflow-x-auto scroll-slim">
                <table className="invoice-items-table w-full min-w-[520px] mb-6 border-collapse">
                    <thead>
                        <tr className="border-b border-slate-900 text-left">
                            <th className="pb-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Désignation de la prestation</th>
                            <th className="pb-2 pl-2 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Qté</th>
                            <th className="pb-2 pl-2 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">P.U. HT</th>
                            <th className="pb-2 pl-2 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">P.U. TTC</th>
                            {displayTvaRates.map((rate) => (
                                <th key={rate} className="pb-2 pl-2 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Base HT ({rate}%)</th>
                            ))}
                            <th className="pb-2 pl-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Total HT</th>
                            <th className="pb-2 pl-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Total TTC</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {previewHasContent ? (
                            lineSummaries.map((line) => (
                                <tr key={line.id} className="invoice-line-row group">
                                    <td className="py-4 align-top">
                                        <p className="font-bold text-slate-800 text-lg">{line.label || 'Prestation non renseignée'}</p>
                                        {settings.enable_cover_count && coverCount ? (
                                            <p className="text-[9px] text-slate-400 uppercase font-bold mt-0.5 tracking-wider">{coverCount} couverts</p>
                                        ) : null}
                                    </td>
                                    <td className="py-4 pl-2 text-center text-slate-600 font-medium text-sm align-top">{line.quantity}</td>
                                    <td className="py-4 pl-2 text-right text-slate-600 font-medium text-sm align-top">{prixUnitaireHt(line)}</td>
                                    <td className="py-4 pl-2 text-right text-slate-600 font-medium text-sm align-top">{prixUnitaireTtc(line)}</td>
                                    {displayTvaRates.map((rate) => {
                                        const detail = line.rateDetails.find((d) => d.rate === rate);
                                        return (
                                            <td key={rate} className="py-4 pl-2 text-right text-slate-700 font-medium text-sm align-top">
                                                {detail && detail.ht > 0 ? toCurrency(detail.ht) : '-'}
                                            </td>
                                        );
                                    })}
                                    <td className="py-4 pl-3 text-right font-black text-slate-900 text-lg align-top">
                                        {toCurrency(line.totalHt)}
                                    </td>
                                    <td className="py-4 pl-3 text-right font-black text-slate-900 text-lg align-top">
                                        {toCurrency(line.totalTtc)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={displayTvaRates.length + 3} className="py-8 text-center text-slate-300 italic text-sm">
                                    Saisissez les montants dans le formulaire pour générer la prestation
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="invoice-bottom-block mt-auto">
                <div className="invoice-summary-section flex justify-end mb-4">
                    <div className="w-[320px] bg-slate-50 p-5 rounded-2xl border border-slate-100 space-y-3 shadow-sm">
                        <div className="flex justify-between text-xs text-slate-500 font-medium">
                            <span>Sous-total Hors Taxes :</span>
                            <span className="text-slate-900">{toCurrency(totalHt)}</span>
                        </div>

                        <div className="space-y-1 pt-2 border-t border-slate-200/60">
                            {displayTvaRates.map((rate) => {
                                const rateTotal = lineSummaries.reduce((sum, line) => {
                                    const detail = line.rateDetails.find((d) => d.rate === rate);
                                    return sum + (detail?.tva || 0);
                                }, 0);
                                if (rateTotal <= 0) return null;
                                return (
                                    <div key={rate} className="flex justify-between text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                        <span>TVA collectée ({rate}%) :</span>
                                        <span className="text-slate-600">{toCurrency(rateTotal)}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-between text-slate-600 text-[10px] font-black uppercase tracking-tight pt-1">
                            <span>Total TVA cumulé :</span>
                            <span>{toCurrency(totalTva)}</span>
                        </div>

                        <div className="flex justify-between text-slate-600 text-[10px] font-black uppercase tracking-tight pt-1 border-t border-slate-200/60">
                            <span>Total TTC :</span>
                            <span>{toCurrency(totalTtcBrut)}</span>
                        </div>

                        {alreadyPaidValue > 0 && (
                            <div className="flex justify-between text-slate-600 text-[10px] font-black uppercase tracking-tight pt-1">
                                <span>Déjà réglé :</span>
                                <span>- {toCurrency(alreadyPaidValue)}</span>
                            </div>
                        )}

                        <div className="pt-4 border-t-2 flex justify-between items-baseline" style={{ borderColor: accentColor, borderTopStyle: 'solid', borderTopWidth: '2px', opacity: 1 }}>
                            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: bottomColor, opacity: 0.7 }}>{bottomLabel}</span>
                            <span className="text-2xl font-black" style={{ color: bottomColor }}>{toCurrency(isSettled ? totalTtcBrut : netToPay)}</span>
                        </div>

                        {/* Plus de pastille « Facture acquittée » sous le total : quand un montant « déjà réglé »
                            solde la facture, le bloc du bas dit déjà « TOTAL RÉGLÉ » et ça suffit — demande
                            d'Emeni (2026-09-16), les factures de l'historique sont des totaux réglés. */}
                    </div>
                </div>

                <div className="invoice-legal-footer pt-4 border-t border-slate-100 text-center">
                    <div className="space-y-1 opacity-80">
                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mb-1">
                            {settings.company_name || user?.company_name || user?.name || 'Votre établissement'}
                        </p>
                        <div className="text-[8px] text-slate-400 leading-relaxed font-medium flex flex-wrap justify-center gap-x-2 gap-y-0.5 uppercase tracking-wide">
                            {settings.rcs_ville && settings.rcs_numero && <span>{`RCS ${settings.rcs_ville} ${settings.rcs_numero}`}</span>}
                            {settings.siret && <span>• SIRET {settings.siret}</span>}
                            {/* Mentions de TVA obligatoires. En franchise en base (art. 293 B du
                                CGI), la mention d'exonération remplace le numéro de TVA
                                intracommunautaire, qui n'a pas à figurer. */}
                            {isFranchiseTva
                                ? <span>• TVA non applicable, art. 293 B du CGI</span>
                                : settings.tva && <span>• TVA {settings.tva}</span>}
                            {!isFranchiseTva && !!settings.tva_on_debits && <span>• TVA acquittée d'après les débits</span>}
                            {settings.ape && <span>• Code APE {settings.ape}</span>}
                            {bankDetails && <span>• {bankDetails}</span>}
                        </div>
                        <div className="text-[8px] text-slate-400 leading-relaxed font-medium flex flex-wrap justify-center gap-x-2 gap-y-0.5 uppercase tracking-wide">
                            {settings.capital && <span>Capital social : {settings.capital}</span>}
                            {settings.siege_social && <span>• {settings.siege_social}</span>}
                            {!settings.siege_social && (settings.address || settings.city) && <span>• {[settings.address, settings.postal_code, settings.city].filter(Boolean).join(' ')}</span>}
                        </div>
                        {/* Mentions obligatoires (Code de commerce, art. L441-10) : conditions d'escompte et taux
                            des pénalités de retard sur toute facture ; indemnité forfaitaire de 40 € réservée aux
                            factures entre professionnels (elle ne peut être réclamée qu'à un client professionnel). */}
                        <div className="text-[8px] text-slate-400 leading-relaxed font-medium flex flex-wrap justify-center gap-x-2 gap-y-0.5 uppercase tracking-wide">
                            <span>Escompte pour paiement anticipé : néant</span>
                            <span>• Pénalités de retard : taux d'intérêt de la BCE majoré de 10 points, exigibles sans rappel</span>
                            {isProfessionalClient && <span>• Indemnité forfaitaire pour frais de recouvrement : 40 €</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

InvoiceDocument.displayName = 'InvoiceDocument';
